import config from "@/config";
import { setJourneyToken } from "./journeyToken";
import { splitFullName } from "@/lib/helper";
import { handleAndTrackError } from "@/lib/helper";
/**
 *
 * @param {*} body
 * @returns
 *
 * @example sendOtp({phone:"999999999"})
 */

// Memoise resolved partner configs (and in-flight requests) so the several
// callers — ThemeContext, PartnerScreen — and any re-renders hit the network
// only ONCE per tag. Without this the journey hammers the endpoint and trips the
// backend's per-IP rate limit. Only successful (exists) results are cached;
// failures (e.g. a transient 429) are not, so they retry on the next call.
const _partnerConfigCache = new Map();
const _partnerConfigInflight = new Map();

/**
 * Validate a partner tag against the backend (no auth). Lets /p/<tag> work for
 * ANY partner created in the B2B dashboard without hardcoding it in the frontend.
 * Returns { exists, tag, source, name, logo_url, primaryColor } — `source` is what
 * signup must send so the lead is attributed to this partner. Resolves
 * { exists:false } on any failure so callers can fall back to NotFound.
 *
 * @example getPartnerConfig("subrat-test")
 */
export const getPartnerConfig = async (tag) => {
  if (!tag) return { exists: false };
  if (_partnerConfigCache.has(tag)) return _partnerConfigCache.get(tag);
  if (_partnerConfigInflight.has(tag)) return _partnerConfigInflight.get(tag);

  const request = (async () => {
    try {
      const response = await fetch(
        `${config.API_URL}/b2b/public/partner-config/${encodeURIComponent(tag)}`,
      );
      if (!response.ok) return { exists: false };
      const data = await response.json();
      if (data?.exists) _partnerConfigCache.set(tag, data); // cache confirmed only
      return data;
    } catch (error) {
      handleAndTrackError(error, { apiPath: "b2b/public/partner-config", tag });
      return { exists: false };
    } finally {
      _partnerConfigInflight.delete(tag);
    }
  })();

  _partnerConfigInflight.set(tag, request);
  return request;
};

export const trackConsent = async (
  method,
  type,
  medium_id = "",
  source = "",
  text,
) => {
  try {
    // Validate parameters
    if (!method || !type) {
      throw new Error("Missing required parameters");
    }

    const body = {
      method,
      type,
      medium_id,
      source,
      text,
    };

    const userIdentifier = localStorage.getItem("user_identifier");
    // const userIdentifier = null;
    const signupToken = localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN);

    const authorizationHeader = userIdentifier
      ? `Short ${userIdentifier}`
      : signupToken
        ? `Bearer ${signupToken}`
        : "";

    const response = await fetch(`${config.API_URL}/user/record-consent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "user/record-consent",
        Authorization: authorizationHeader,
      },
      body: JSON.stringify(body),
    });

    // `record-consent` replies 201 with an EMPTY body, which is a valid REST response but makes
    // `response.json()` throw "Unexpected end of JSON input". That threw on EVERY signup — the
    // consent was saved server-side (201), yet this returned {success:false} and logged a console
    // error via the catch. A no-content success is a success; parse only when there is a body.
    const raw = await response.text();
    const data = raw ? JSON.parse(raw) : null;
    return { success: response.ok, data };
  } catch (error) {
    handleAndTrackError(error, {
      apiPath: "user/record-consent",
      body: { method, type, medium_id, source, text },
    });
    return { success: false, error };
  }
};

export const sendOtp = async (body) => {
  try {
    const response = await fetch(`${config.API_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/login",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || "Failed to send OTP.";

      const error = new Error(errorMessage);
      error.response = response;
      error.errorData = errorData;

      handleAndTrackError(error, {
        apiPath: "auth/login",
        body,
        errorData,
      });
    }

    const data = await response.json();
    setJourneyToken(data.token);

    return data;
  } catch (error) {
    handleAndTrackError(error, { apiPath: "auth/login", body });
  }
};

export const sendOtpV2 = async (body) => {
  try {
    const response = await fetch(`${config.API_URL}/v2/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/login",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || "Failed to send OTP.";

      const error = new Error(errorMessage);
      error.response = response;
      error.errorData = errorData;

      handleAndTrackError(error, {
        apiPath: "auth/login",
        body,
        errorData,
      });
    }

    const data = await response.json();
    setJourneyToken(data.token);

    return data;
  } catch (error) {
    handleAndTrackError(error, { apiPath: "auth/login", body });
  }
};

export const loginLeadB2B = async (body) => {
  try {
    const response = await fetch(`${config.API_URL}/b2b/auth/login-lead`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/login",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || "Failed to send OTP.";

      const error = new Error(errorMessage);
      error.response = response;
      error.errorData = errorData;

      handleAndTrackError(error, {
        apiPath: "auth/login",
        body,
        errorData,
      });

      return errorData;
    }

    const data = await response.json();
    // setJourneyToken(data.token);

    return data;
  } catch (error) {
    handleAndTrackError(error, { apiPath: "auth/login", body });
  }
};

export const signUpV2 = async (body) => {
  try {
    const response = await fetch(`${config.API_URL}/v2/auth/sign-up`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/login",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || "Failed to send OTP.";

      const error = new Error(errorMessage);
      error.response = response;
      error.errorData = errorData;

      handleAndTrackError(error, {
        apiPath: "auth/login",
        body,
        errorData,
      });
    }

    const data = await response.json();
    setJourneyToken(data.token);

    return data;
  } catch (error) {
    handleAndTrackError(error, { apiPath: "auth/login", body });
  }
};

/**
 * Verify otp
 *
 * @param {*} body
 * @returns
 *
 * @example verifyOtp({
 *  "token": "sample_token",
 *  "otp": "123456"
 *  })
 */

export const verifyOtp = async (body, api = "auth/verify-otp") => {
  try {
    const response = await fetch(`${config.API_URL}/${api}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/verify-otp",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorMessage = await response.text();
      const error = new Error(
        `Network error: ${errorMessage || "Unknown error"}`,
      );
      error.response = response;

      // Centralized error handling
      handleAndTrackError(error, {
        apiEndpoint: `${config.API_URL}/auth/verify-otp`,
        requestBody: body,
        responseStatus: response?.status,
        responseText: errorMessage,
      });
    }

    const data = await response.json();

    if (data?.token) {
      setJourneyToken(data.token);
    }

    return data;
  } catch (error) {
    handleAndTrackError(error, {
      apiEndpoint: `${config.API_URL}/auth/verify-otp`,
      requestBody: body,
    });
  }
};

export const verifyOtpB2B = async (
  body,
  api = "auth/verify-otp",
  identifierOverride
) => {
  // `identifierOverride` lets a caller that already has a reliable value (e.g. straight from
  // useParams(), never localStorage) pass it in directly instead of relying on this function's
  // own localStorage read having landed in time — see SignUpScreen.jsx for why that matters.
  // Existing callers that don't pass a third argument get the exact previous behavior.
  const userIdentifier =
    identifierOverride || localStorage.getItem("user_identifier");
  const signupToken = localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN);

  const authorizationHeader = userIdentifier
    ? `Short ${userIdentifier}`
    : signupToken
      ? `Bearer ${signupToken}`
      : "";

  try {
    const response = await fetch(`${config.API_URL}/${api}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "auth/verify-otp",
        Authorization: authorizationHeader,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorMessage = await response.text();
      const error = new Error(
        `Network error: ${errorMessage || "Unknown error"}`,
      );
      error.response = response;

      // Centralized error handling
      handleAndTrackError(error, {
        apiEndpoint: `${config.API_URL}/auth/verify-otp`,
        requestBody: body,
        responseStatus: response.status,
        responseText: errorMessage,
      });
    }

    const data = await response.json();

    if (data?.token) {
      setJourneyToken(data.token);
    }

    return data;
  } catch (error) {
    handleAndTrackError(error, {
      apiEndpoint: `${config.API_URL}/auth/verify-otp`,
      requestBody: body,
    });
  }
};

export const submitOnboardingData = async (formData) => {
  const { first_name, middle_name, last_name } = splitFullName(
    formData.full_name,
  );
  const { full_name, ...rest } = formData;

  const body = {
    ...rest,
    first_name,
    middle_name,
    last_name,
  };

  try {
    const response = await fetch(`${config.API_URL}/users/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "users/create",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to create user: ", error);
    throw error;
  }
};

export const saveLandlord = async (formData) => {
  const { first_name, middle_name, last_name } = splitFullName(
    formData.landlord_name,
  );
  const { landlord_name, ...rest } = formData;

  const body = {
    ...rest,
    first_name,
    middle_name,
    last_name,
  };

  try {
    const response = await fetch(`${config.API_URL}/landlords`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "save-landlords",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to create landlord: ", error);
    throw error;
  }
};

export const savePropertyDetails = async (formData) => {
  try {
    const response = await fetch(`${config.API_URL}/landlords`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "save-prop",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to create landlord: ", error);
    throw error;
  }
};

export const editFlatmate = async (formData) => {
  try {
    const response = await fetch(`${config.API_URL}/property/edit-flatmate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-event-type": "property/edit-flatmate",
        Authorization: `Bearer ${localStorage.getItem(
          config.LOCAL_KEYS.SIGNUP_TOKEN,
        )}`,
      },
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to edit flatmate: ", error);
    throw error;
  }
};
